import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  columnCards,
  filterDeliveries,
  filterStacks,
  sortDeliveredCards,
  stackDelivered,
  supplierById,
  type SupplierNameOf,
} from '@d-orca/shared/client/logistics';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import type { Delivery, Supplier } from '@interfaces/ILogistics';
import { ArrowLeftRightIcon } from '@utils/icons';
import { readDeliveredViewPref, writeDeliveredViewPref, type DeliveredView } from '@utils/deliveredViewPref';
import { Panel } from '../Panel';
import { DeliveryHistoryRow, MaterialHistoryRow } from './DeliveryHistoryRows';
import { ExpandableList, ExpandableListEmpty } from './ExpandableList';
import { NoMatches } from './NoMatches';
import styles from './DeliveryHistory.module.css';

interface IDeliveryHistoryProps {
  /** The site's whole order list; the panel keeps the synced ones. */
  deliveries: Delivery[];
  suppliers: Supplier[];
  tz: string;
}

/**
 * Delivery History — every synced order, off the pipeline (a synced arrival
 * is settled: its stock is counted and it never drags). The rows are Waste
 * Tracking's (`ExpandableListRow`), and the switch the Delivered column used to
 * carry lives here: one row per delivery, or one per material with its total.
 *
 * Its search is its own. The Materials search above filters the panels that
 * are about what is happening now; this one is about what already happened,
 * and also matches suppliers, which every row prints.
 */
export function DeliveryHistory({ deliveries, suppliers, tz }: IDeliveryHistoryProps) {
  const { t } = useTranslation();
  // A reading preference, remembered across reloads.
  const [view, setView] = useState<DeliveredView>(readDeliveredViewPref);
  const [query, setQuery] = useState('');
  /** The one open row — `view:key`, so switching views closes it. */
  const [open, setOpen] = useState<string | null>(null);

  const supplierOf: SupplierNameOf = (delivery) => supplierById(suppliers, delivery.supplierId)?.name ?? null;
  const synced = deliveries.filter((delivery) => delivery.isSynced);
  const matched = filterDeliveries(synced, query, supplierOf);
  const otherCount = view === 'delivery' ? stackDelivered(synced).length : synced.length;

  /** The open/toggle wiring a row needs, keyed by its `view:key` id. */
  const rowState = (id: string) => ({
    open: open === id,
    onToggle: () => setOpen(open === id ? null : id),
    supplierOf,
    tz,
  });
  const rows =
    view === 'delivery'
      ? sortDeliveredCards(columnCards(matched)).map(({ face }) => (
          <DeliveryHistoryRow key={face.ref} {...rowState(`delivery:${face.ref}`)} order={face} />
        ))
      : sortDeliveredCards(filterStacks(stackDelivered(matched), query, supplierOf)).map((stack) => (
          <MaterialHistoryRow key={stack.key} {...rowState(`material:${stack.key}`)} stack={stack} />
        ));

  const switchView = () => {
    const next: DeliveredView = view === 'delivery' ? 'material' : 'delivery';
    writeDeliveredViewPref(next);
    setView(next);
  };

  return (
    <Panel
      title={t('logistics.history.title')}
      subtitle={t('logistics.history.subtitle')}
      headerRight={
        <div className={styles.controls}>
          <SearchInput
            className={styles.search}
            value={query}
            onChange={setQuery}
            placeholder={t('logistics.history.searchPlaceholder')}
            ariaLabelClear={t('logistics.history.searchClear')}
          />
          {/* Labelled with the view it switches TO, and how many rows that
              view lists. */}
          <button type="button" className={styles.viewSwitch} onClick={switchView}>
            <ArrowLeftRightIcon size={12} />
            {t(view === 'delivery' ? 'logistics.history.viewPerMaterial' : 'logistics.history.viewPerDelivery', {
              n: otherCount,
            })}
          </button>
        </div>
      }
    >
      {synced.length === 0 ? (
        <ExpandableListEmpty>{t('logistics.history.empty')}</ExpandableListEmpty>
      ) : rows.length === 0 ? (
        <NoMatches />
      ) : (
        <ExpandableList className={styles.list}>{rows}</ExpandableList>
      )}
    </Panel>
  );
}
